import React, { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { ModalBody, Card, CardBody, Button, FormGroup, Label, Col } from 'reactstrap';
import { BASE_URL, API } from 'CONST';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload, faPaperclip } from '@fortawesome/free-solid-svg-icons';
import { fileDownload } from 'helpers/helpers';
import { get, post } from 'helpers/requests';
import FileTable from '../../shared/FileTable';
import BaseModal from "../../UI/BaseModal";


const RepositoryModal = ({ isOpen, toggle, dataCredito, dataDeudor, otrosDocumentos: otrosDocumentosProp }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [expandedPolicies, setExpandedPolicies] = useState({});
  const [expandedCarpetas, setExpandedCarpetas] = useState({});
  const [documentos, setDocumentos] = useState([]);
  const [otrosDocumentos, setOtrosDocumentos] = useState(otrosDocumentosProp || []);
  const [isUploading, setIsUploading] = useState(false);
  const rutDeudor = dataDeudor?.rut || '';
  const deudorId = dataDeudor?.id || '';
  const loginState = useSelector((state) => state.login);
  const [isLoadingOtros, setIsLoadingOtros] = useState(false);

  // Helper function to check if a date is within the last week
  const isWithinLastWeek = (date) => {
    if (!date) return false;
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    const documentDate = new Date(date);
    return documentDate >= oneWeekAgo;
  };

  const handleGetDocumentosDeudor = () => {
    if (!deudorId) return;
    
    get(`${BASE_URL}${API.documentosdeudordirect}/?deudor_id=${deudorId}`)
      .then((response) => {
        if (response && response.length > 0) {
          setOtrosDocumentos(response);
        } else {
          setOtrosDocumentos([]);
        }
      })
      .catch((error) => {
        console.error('Error al obtener documentos del deudor:', error);
      });
  }

  const handleFileSelect = (docId) => {
    setSelectedFiles(prev => 
      prev.includes(docId) 
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  const handleUploadFiles = async () => {
    if (documentos.length === 0 || !deudorId) return;

    setIsUploading(true);
    
    try {
      const response = await post(`${BASE_URL}${API.documentosdeudordirect}/`, {
        deudor: deudorId,
        documentos: documentos.map(d => d.uuid),
        created_by: parseInt(loginState.user.sub, 10)
      });

      if (response) {
        setDocumentos([]);
        handleGetDocumentosDeudor();
      }
    } catch (error) {
      console.error('Error uploading documents:', error);
    } finally {
      setIsUploading(false);
    }
  };

  useEffect(() => {
    if (isOpen && deudorId) {
      handleGetDocumentosDeudor();
    }
  }, [isOpen, deudorId]);

  useEffect(() => {
    if (otrosDocumentosProp) {
      setOtrosDocumentos(otrosDocumentosProp);
    }
  }, [otrosDocumentosProp]);

  const handleDownloadSelected = () => {
    selectedFiles.forEach(docId => {
      fileDownload(`${BASE_URL}${API.downloadDocumento}/`, docId);
    });
    setSelectedFiles([]);
  };

  const groupedData = useMemo(() => {
    if (!dataCredito.data) return {};
    
    const grouped = {};
    
    dataCredito.data.forEach(movement => {
      // eslint-disable-next-line camelcase
      const { poliza, asegurado_nombre } = movement;
      
      if (!grouped[poliza]) {
        grouped[poliza] = {
          aseguradoNombre: asegurado_nombre,
          movements: []
        };
      }
      
      grouped[poliza].movements.push(movement);
    });
    
    return grouped;
  }, [dataCredito.data]);

  const groupedByCarpeta = useMemo(() => {
    if (!dataCredito.data) return {};
    
    const grouped = {};
    
    dataCredito.data.forEach(movement => {
      // eslint-disable-next-line camelcase
      const { carpeta_tributaria_id, asegurado_nombre } = movement;
      // eslint-disable-next-line camelcase
      if (carpeta_tributaria_id) {
        // eslint-disable-next-line camelcase
        if (!grouped[carpeta_tributaria_id]) {
          grouped[carpeta_tributaria_id] = {
            aseguradoNombre: asegurado_nombre,
            movements: []
          };
        }
        
        grouped[carpeta_tributaria_id].movements.push(movement);
      }
    });
    
    return grouped;
  }, [dataCredito.data]);

  useEffect(() => {
    // Auto-expand if only one policy
    if (Object.keys(groupedData).length === 1) {
      const [singlePolicy] = Object.keys(groupedData);
      setExpandedPolicies({ [singlePolicy]: true });
    } else {
      setExpandedPolicies({});
    }
    
    // Auto-expand if only one carpeta
    if (Object.keys(groupedByCarpeta).length === 1) {
      const [singleCarpeta] = Object.keys(groupedByCarpeta);
      setExpandedCarpetas({ [singleCarpeta]: true });
    } else {
      setExpandedCarpetas({});
    }
  }, [groupedData, groupedByCarpeta]);

  const togglePolicy = (poliza) => {
    setExpandedPolicies(prev => ({
      ...prev,
      [poliza]: !prev[poliza]
    }));
  };

  const toggleCarpeta = (carpetaId) => {
    setExpandedCarpetas(prev => ({
      ...prev,
      [carpetaId]: !prev[carpetaId]
    }));
  };

  const clearSelectedFiles = () => {
    setSelectedFiles([]);
  };

  const groupedOtrosDocumentos = useMemo(() => {
    const grouped = {};
    otrosDocumentos.forEach((docGroup) => {
      const key = docGroup.tipo_natural_key || 'otros';
      const label = docGroup.tipo_nombre || 'Otros Documentos';
      if (!grouped[key]) {
        grouped[key] = { label, docs: [] };
      }
      grouped[key].docs.push(docGroup);
    });
    return grouped;
  }, [otrosDocumentos]);

  const renderOtrosDocumentos = () => {
    if (otrosDocumentos.length === 0) return null;

    return Object.entries(groupedOtrosDocumentos).map(([tipoKey, { label, docs }]) => {
      const groupId = `otros-${tipoKey}`;
      const hasRecentDocs = docs.some(doc => isWithinLastWeek(doc.upload_date));

      return (
        <div className="repositorio-poliza-group" key={groupId}>
          <div
            className="repositorio-poliza-header clickable"
            onClick={() => togglePolicy(groupId)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                togglePolicy(groupId);
              }
            }}
            tabIndex={0}
            role="button"
            aria-expanded={expandedPolicies[groupId]}
            aria-controls={`policy-content-${groupId}`}
          >
            <h3 className="repositorio-poliza-title">
              <span className="toggle-icon">
                {expandedPolicies[groupId] ? '▼' : '►'}
              </span>
              📋 {label}
              {hasRecentDocs && (
                <span
                  style={{
                    marginLeft: '8px',
                    background: '#ff4444',
                    color: 'white',
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    fontSize: '11px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold'
                  }}
                  title="Documentos recientes (última semana)"
                >
                  <FontAwesomeIcon icon={faPaperclip} />
                </span>
              )}
              <span className="badge movement-count">
                {docs.length} documento{docs.length !== 1 ? 's' : ''}
              </span>
            </h3>
          </div>

          {expandedPolicies[groupId] && (
            <div id={`policy-content-${groupId}`} className="repositorio-movements-container">
              <div className="repositorio-movements-grid">
                {docs.map((docGroup, index) => {
                  const isRecent = isWithinLastWeek(docGroup.upload_date);
                  return (
                    <Card key={`${groupId}-${index}`} className="repositorio-movement">
                      <CardBody className="repositorio-card-body">
                        <div className="repositorio-header">
                          <div className="repositorio-main-info">
                            <h5 className="repositorio-title">
                              Subido el {new Date(docGroup.upload_date).toLocaleDateString()}
                              {isRecent && (
                                <span
                                  style={{
                                    marginLeft: '8px',
                                    background: '#ff4444',
                                    color: 'white',
                                    borderRadius: '50%',
                                    width: '18px',
                                    height: '18px',
                                    fontSize: '10px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 'bold'
                                  }}
                                  title="Documento reciente"
                                >
                                  <FontAwesomeIcon icon={faPaperclip} />
                                </span>
                              )}
                            </h5>
                            <div className="repositorio-subtitle">
                              Subido por: {docGroup.created_by_name || `Usuario ID: ${docGroup.created_by}`}
                            </div>
                          </div>
                        </div>

                        <div className="repositorio-files-list">
                          {docGroup.documentos_con_nombres?.length > 0 ? (
                            docGroup.documentos_con_nombres.map(doc => (
                              <div key={doc.id} className="repositorio-file-container">
                                <label htmlFor={`checkbox-${doc.id}`} className="repositorio-checkbox-label">
                                  <input
                                    id={`checkbox-${doc.id}`}
                                    type="checkbox"
                                    checked={selectedFiles.includes(doc.id)}
                                    onChange={() => handleFileSelect(doc.id)}
                                    className="repositorio-checkbox"
                                  />
                                  <span className="repositorio-checkmark" />
                                </label>
                                <div className="repositorio-file-item">
                                  <span className="repositorio-file-icon">📄</span>
                                  <span className="repositorio-file-name" title={doc.name}>
                                    {doc.name}
                                  </span>
                                  <Button
                                    color="link"
                                    size="sm"
                                    className="repositorio-download-btn"
                                    onClick={() => fileDownload(`${BASE_URL}${API.downloadDocumento}/`, doc.id)}
                                  >
                                    <FontAwesomeIcon icon={faDownload} />
                                  </Button>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="repositorio-empty">Sin documentos</div>
                          )}
                        </div>
                      </CardBody>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      );
    });
  };

  const renderCarpetaTributaria = () => {
    if (Object.keys(groupedByCarpeta).length === 0) return null;

    return (
      <>
        {Object.entries(groupedByCarpeta).map(([carpetaId, carpetaData]) => {
          const hasRecentDocs = carpetaData.movements.some(movement => 
            isWithinLastWeek(movement.fecha_solicitud)
          );

          return (
            <div key={`carpeta-${carpetaId}`} className="repositorio-poliza-group">
              <div 
                className="repositorio-poliza-header clickable"
                onClick={() => toggleCarpeta(carpetaId)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleCarpeta(carpetaId);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-expanded={expandedCarpetas[carpetaId]}
                aria-controls={`carpeta-content-${carpetaId}`}
              >
                <h3 className="repositorio-poliza-title">
                  <span className="toggle-icon">
                    {expandedCarpetas[carpetaId] ? '▼' : '►'}
                  </span>
                  📁 Carpeta Tributaria: {carpetaData.aseguradoNombre}
                  {hasRecentDocs && (
                    <span 
                      style={{
                        marginLeft: '8px',
                        background: '#ff4444',
                        color: 'white',
                        borderRadius: '50%',
                        width: '20px',
                        height: '20px',
                        fontSize: '11px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold'
                      }}
                      title="Documentos recientes (última semana)"
                    >
                      <FontAwesomeIcon icon={faPaperclip} />
                    </span>
                  )}
                  <span className="badge movement-count">
                    {carpetaData.movements.length} documento{carpetaData.movements.length !== 1 ? 's' : ''}
                  </span>
                </h3>
              </div>
              
              {expandedCarpetas[carpetaId] && (
                <div id={`carpeta-content-${carpetaId}`} className="repositorio-movements-container">
                  <div className="repositorio-movements-grid">
                    {carpetaData.movements.map((movement, index) => {
                      const isRecent = isWithinLastWeek(movement.fecha_solicitud);
                      return (
                        <Card key={`carpeta-${carpetaId}-${index}`} className="repositorio-movement">
                          <CardBody className="repositorio-card-body">
                            <div className="repositorio-header">
                              <div className="repositorio-main-info">
                                <h5 className="repositorio-title">
                                  {movement.asegurado_nombre}
                                  {isRecent && (
                                    <span 
                                      style={{
                                        marginLeft: '8px',
                                        background: '#ff4444',
                                        color: 'white',
                                        borderRadius: '50%',
                                        width: '18px',
                                        height: '18px',
                                        fontSize: '10px',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: 'bold'
                                      }}
                                      title="Documento reciente"
                                    >
                                      <FontAwesomeIcon icon={faPaperclip} />
                                    </span>
                                  )}
                                </h5>
                                <div className="repositorio-subtitle">
                                  {movement.fecha_solicitud}
                                </div>
                                <div className="repositorio-subtitle">
                                  Póliza: {movement.poliza}
                                </div>
                              </div>
                            </div>
                           
                            <div className="repositorio-files-list">
                              {movement.carpeta_tributaria_filename ? (
                                <div key={`carpeta-file-${movement.carpeta_tributaria_id}`} className="repositorio-file-container">
                                  <label htmlFor={`checkbox-${movement.carpeta_tributaria_id}`} className="repositorio-checkbox-label">
                                    <input
                                      id={`checkbox-${movement.carpeta_tributaria_id}`}
                                      type="checkbox"
                                      checked={selectedFiles.includes(movement.carpeta_tributaria_id)}
                                      onChange={() => handleFileSelect(movement.carpeta_tributaria_id)}
                                      className="repositorio-checkbox"
                                    />
                                    <span className="repositorio-checkmark" />
                                  </label>
                                  <div className="repositorio-file-item">
                                    <span className="repositorio-file-icon">📄</span>
                                    <span className="repositorio-file-name" title={movement.carpeta_tributaria_filename}>
                                      {movement.carpeta_tributaria_filename}
                                    </span>
                                    <Button
                                      color="link"
                                      size="sm"
                                      className="repositorio-download-btn"
                                      onClick={() => fileDownload(`${BASE_URL}${API.downloadDocumento}/`, movement.carpeta_tributaria_id)}
                                    >
                                      <FontAwesomeIcon icon={faDownload} />
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <div className="repositorio-empty">Sin documentos</div>
                              )}
                            </div>
                          </CardBody>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </>
    );
  };

  return (
    <BaseModal
      modalOpen={isOpen}
      handleModal={() => toggle(false)}
      title="Repositorio de Archivos"
      size="xl"
      type="repositorio-modal repositorio-modal-dynamic"
    >
      <ModalBody className="repositorio-modal-content">

        <FormGroup row>
          <Label sm={2} for="documentos">
            Subir Documentos
            {documentos.length > 0 && (
              <Button 
                color="primary" 
                onClick={handleUploadFiles}
                className="mt-2"
              >
                Cargar archivos
              </Button>
            )}
          </Label>
          <Col sm={10} style={{ paddingLeft: 0, paddingBottom: 15 }}>
            <FileTable
            // eslint-disable-next-line react/jsx-boolean-value
              isRepositoryModal={true}
              newFiles={documentos}
              handleAdd={(file) => {
                setDocumentos((prevState) => [...prevState, file]);
              }}
              handleDeleteNew={(file) => {
                setDocumentos((prevState) => prevState.filter((doc) => doc.uuid !== file.uuid));
              }}
              uploadUrl={`${BASE_URL}${API.uploadDocumento}/`}
              individualMaxFileSize={13}
              globalMaxFileSize={20}
              loginIdToken=''
            />
          </Col>
        </FormGroup>

        {selectedFiles.length > 0 && (
          <div className="repositorio-actions">
            <Button
              color="primary"
              onClick={handleDownloadSelected}
              className="mb-3"
            >
              Descargar Seleccionados ({selectedFiles.length})
            </Button>
            <Button
              color="secondary"
              onClick={clearSelectedFiles}
              className="mb-3 ml-2"
            >
              Limpiar selección
            </Button>
          </div>
        )}
        
        <div className="repositorio-container">
          {renderOtrosDocumentos()}
          
          {/* Sección Carpeta Tributaria */}
          {renderCarpetaTributaria()}
          
          {/* Sección Poliza */}
          {Object.keys(groupedData).length > 0 ? (
            Object.entries(groupedData).map(([poliza, polizaData]) => {
              // Check if any movement in this poliza is recent
              const hasRecentDocs = polizaData.movements.some(movement => 
                (movement.documentos_ids?.length > 0 || movement.carpeta_tributaria_id) && 
                isWithinLastWeek(movement.fecha_solicitud)
              );

              return (
                <div key={poliza} className="repositorio-poliza-group">
                  <div 
                    className="repositorio-poliza-header clickable"
                    onClick={() => togglePolicy(poliza)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        togglePolicy(poliza);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-expanded={expandedPolicies[poliza]}
                    aria-controls={`policy-content-${poliza}`}
                  >
                    <h3 className="repositorio-poliza-title">
                      <span className="toggle-icon">
                        {expandedPolicies[poliza] ? '▼' : '►'}
                      </span>
                      📋 Póliza: {poliza} - {polizaData.aseguradoNombre}
                      {hasRecentDocs && (
                        <span 
                          style={{
                            marginLeft: '8px',
                            background: '#ff4444',
                            color: 'white',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            fontSize: '11px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold'
                          }}
                          title="Documentos recientes (última semana)"
                        >
                          <FontAwesomeIcon icon={faPaperclip} />
                        </span>
                      )}
                      <span className="badge movement-count">
                        {polizaData.movements.length} movimiento{polizaData.movements.length !== 1 ? 's' : ''}
                      </span>
                    </h3>
                  </div>
                  
                  {expandedPolicies[poliza] && (
                    <div id={`policy-content-${poliza}`} className="repositorio-movements-container">
                      <div className="repositorio-movements-grid">
                        {polizaData.movements.map((movement, index) => {
                          const hasDocuments = movement.documentos_ids?.length > 0;
                          const isRecent = isWithinLastWeek(movement.fecha_solicitud);
                          const showClip = hasDocuments && isRecent;

                          return (
                            <Card key={index} className="repositorio-movement">
                              <CardBody className="repositorio-card-body">
                                <div className="repositorio-header">
                                  <div className="repositorio-main-info">
                                    <h5 className="repositorio-title">
                                      {movement.asegurado_nombre}
                                      {showClip && (
                                        <span 
                                          style={{
                                            marginLeft: '8px',
                                            background: '#ff4444',
                                            color: 'white',
                                            borderRadius: '50%',
                                            width: '18px',
                                            height: '18px',
                                            fontSize: '10px',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontWeight: 'bold'
                                          }}
                                          title="Documentos recientes"
                                        >
                                          <FontAwesomeIcon icon={faPaperclip} />
                                        </span>
                                      )}
                                    </h5>
                                    <div className="repositorio-subtitle">
                                      {movement.fecha_solicitud}
                                    </div>
                                    <div className="repositorio-subtitle">
                                      Monto: {movement.monto_solicitado}
                                    </div>
                                    <div className="repositorio-subtitle">
                                      Estado: {movement.estado_descripcion}
                                    </div>
                                  </div>
                                </div>
                               
                                <div className="repositorio-files-list">
                                  {movement.documentos_info?.length > 0 ? (
                                    movement.documentos_info.map((filename, docIndex) => (
                                      <div key={`${movement.documentos_ids[docIndex]}-${docIndex}`} className="repositorio-file-container">
                                        <label htmlFor={`checkbox-${movement.documentos_ids[docIndex]}`} className="repositorio-checkbox-label">
                                          <input
                                            id={`checkbox-${movement.documentos_ids[docIndex]}`}
                                            type="checkbox"
                                            checked={selectedFiles.includes(movement.documentos_ids[docIndex])}
                                            onChange={() => handleFileSelect(movement.documentos_ids[docIndex])}
                                            className="repositorio-checkbox"
                                          />
                                          <span className="repositorio-checkmark" />
                                        </label>
                                        <div className="repositorio-file-item">
                                          <span className="repositorio-file-icon">📄</span>
                                          <span className="repositorio-file-name" title={filename}>
                                            {filename}
                                          </span>
                                          <Button
                                            color="link"
                                            size="sm"
                                            className="repositorio-download-btn"
                                            onClick={() => fileDownload(`${BASE_URL}${API.downloadDocumento}/`, movement.documentos_ids[docIndex])}
                                          >
                                            <FontAwesomeIcon icon={faDownload} />
                                          </Button>
                                        </div>
                                      </div>
                                    ))
                                  ) : (
                                    <div className="repositorio-empty">Sin documentos</div>
                                  )}
                                </div>
                              </CardBody>
                            </Card>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="repositorio-empty-state">
              No hay movimientos disponibles
            </div>
          )}
        </div>
      </ModalBody>
    </BaseModal>
  );
};

RepositoryModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  dataCredito: PropTypes.object.isRequired,
  dataDeudor: PropTypes.object.isRequired,
  otrosDocumentos: PropTypes.array
};
RepositoryModal.defaultProps = {
  otrosDocumentos: []
};
export default RepositoryModal;